/**
 * ChangelogPopup — Shows once per user per new changelog entry on login.
 * 
 * Layout: Image/GIF on top → Title + Description → Dismiss + Link buttons
 * 
 * Fetches /api/changelog/unseen on mount. If there are unseen entries,
 * shows the most recent one as a modal popup. On dismiss, calls
 * /api/changelog/:id/dismiss and closes.
 */
import { HugeiconsIcon } from '@hugeicons/react';
import { Cancel01Icon as X, LinkSquare01Icon as ExternalLink, MegaphoneIcon as Megaphone } from '@hugeicons/core-free-icons';
import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { useAuthFetch } from '@/hooks/useAuthFetch';

interface ChangelogEntry {
  id: number;
  title: string;
  slug: string;
  version: string;
  content: string;
  image: string | null;
  link: string | null;
  type: string;
  published_at: string;
  created_at: string;
}

export function ChangelogPopup() {
  const [entry, setEntry] = useState<ChangelogEntry | null>(null);
  const [open, setOpen] = useState(false);
  const authFetch = useAuthFetch();

  useEffect(() => {
    const timer = setTimeout(async () => {
      try {
        // Fetch latest changelog entry
        const res = await authFetch('/api/changelog/latest');
        if (!res.ok) return;
        const data = await res.json();
        if (!data.entry) return;

        // Check localStorage if user already saw this entry
        const lastSeenId = localStorage.getItem('genofx:changelog_seen_id');
        if (lastSeenId === String(data.entry.id)) return;

        setEntry(data.entry);
        setOpen(true);
      } catch {}
    }, 2000);
    return () => clearTimeout(timer);
  }, [authFetch]);

  const handleDismiss = async () => {
    if (entry) {
      localStorage.setItem('genofx:changelog_seen_id', String(entry.id));
    }
    setOpen(false);
  };

  const handleLink = () => {
    if (entry?.link) {
      window.open(entry.link, '_blank');
    } else {
      window.open('/changelog', '_blank');
    }
    handleDismiss();
  };

  if (!entry) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleDismiss(); }}>
      <DialogContent className="sm:max-w-md p-0 overflow-hidden rounded-2xl border-accent/20">
        {/* Image/GIF */}
        {entry.image && (
          <div className="w-full h-48 bg-muted overflow-hidden">
            <img
              src={entry.image}
              alt={entry.title}
              className="w-full h-full object-cover"
            />
          </div>
        )}
        {!entry.image && (
          <div className="w-full h-32 bg-gradient-to-br from-accent/20 to-accent/5 flex items-center justify-center">
            <HugeiconsIcon icon={Megaphone} className="h-12 w-12 text-accent/50"  />
          </div>
        )}

        {/* Content */}
        <div className="p-6 space-y-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              {entry.version && (
                <span className="text-xs font-mono bg-accent/10 text-accent px-2 py-0.5 rounded-full">
                  v{entry.version}
                </span>
              )}
              <span className="text-xs text-muted-foreground">
                {new Date(entry.published_at || entry.created_at).toLocaleDateString()}
              </span>
            </div>
            <h3 className="text-lg font-bold">{entry.title}</h3>
          </div>

          <p className="text-sm text-muted-foreground leading-relaxed line-clamp-4">
            {entry.content?.replace(/[#*_`]/g, '').slice(0, 200)}
            {(entry.content?.length || 0) > 200 ? '...' : ''}
          </p>

          {/* Buttons */}
          <div className="flex items-center gap-3 pt-2">
            <Button
              variant="default"
              size="sm"
              onClick={handleLink}
              className="flex-1"
            >
              <HugeiconsIcon icon={ExternalLink} className="h-3.5 w-3.5 mr-1.5"  />
              View Details
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleDismiss}
              className="text-muted-foreground"
            >
              Dismiss
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
